import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, Code, Group, Loader, Modal, Stack, Text, ThemeIcon, Timeline,
} from '@mantine/core'
import {
  IconCheck, IconAlertTriangle, IconShieldCheck, IconClock,
} from '@tabler/icons-react'

import { getProvisioningRequest, type ProvisioningRequest, type ProvisioningStep } from '@/api/waf'
import { formatApiError } from '@/api/errors'
import { COLOR } from '@/pages/recursos/components/HardRestart/tokens'
import { formatElapsed, useTicker } from '@/pages/recursos/components/HardRestart/useTicker'

/**
 * Modal que mostra o provisionamento acontecendo, para quem clicou.
 *
 * Desenho em `docs/superpowers/specs/2026-08-14-modal-de-progresso-do-provisionamento-de-waf-design.md`
 * (revisão 2), e o acabamento visual segue o modal de Deploy do Hard Restart — decisão
 * do Carlos em 14/08, ao ver que a primeira versão ficou "muito simples" ao lado dele.
 * Daí virem daqui o `COLOR` e o `formatElapsed`/`useTicker`: são os tokens da marca e o
 * relógio vivo que aquele modal já usa, e reusá-los é o que faz os dois parecerem o mesmo
 * produto.
 *
 * **O que NÃO foi copiado, e por quê:** o cardiograma. No Deploy ele é alimentado pela
 * saída real do comando SSM, que chega em fluxo. Provisionamento de WAF são marcos
 * DISCRETOS, sem stream — uma linha animada aqui seria decoração sugerindo vivacidade que
 * o dado não sustenta. É a inversão do princípio que criou este modal ("vazio se lê como
 * defeito"): encher a tela para parecer vivo é tão enganoso quanto deixá-la muda.
 *
 * **O que a medição impôs**, agora com DUAS amostras de produção — e a segunda corrigiu a
 * primeira:
 *
 * | pedido | total | espera de propagação | tentativas |
 * |---|---|---|---|
 * | 10 (12/08) | 352 s | 331,8 s (94%) | — |
 * | 11 (15/08) | **691 s** | **604,9 s (88%)** | **9** |
 * | 14 (17/08) | **186 s** | — | — |
 * | 26 (26/08) | **718 s** | **609 s (85%)** | **45 recusas** |
 * | 29 (26/08) | **146 s** | **61 s (42%)** | **1** |
 *
 * A primeira versão deste arquivo dizia `~5 min` na propagação, derivado de n=1. Com n=2 o
 * rótulo virou `5 a 10 min`. A terceira amostra desmentiu o piso e ele virou `3 a 10 min`.
 * **Com n=5, o TETO também caiu:** o pedido 26 levou 609 s só nessa etapa — dez minutos e
 * nove segundos, acima do limite que o próprio rótulo prometia. E o 29, na MESMA conta e no
 * mesmo dia, levou 61 s.
 *
 * A dispersão é da AWS, não do fluxo: o CloudTrail da conta registra **45 recusas** de
 * `AssociateWebACL` com `WAFUnavailableEntityException` no pedido 26 (a 46ª passou) e uma
 * única tentativa no 29. Nenhuma delas foi `AccessDenied`.
 *
 * **Terceira faixa não se conserta com uma quarta faixa.** Prazo que a plataforma não
 * controla deixa de ser prometido: o marco diz `depende da AWS`, e quem informa o operador é
 * o tempo DECORRIDO, que é dado que a plataforma tem de verdade. (TASK-10173)
 *
 * O que NÃO mudou: a propagação segue sendo a esmagadora maioria do tempo (88–94%), e é por
 * isso que o bloqueio termina ali. E o `gmud_apply_scheduler` tem tick de **60 s**, então
 * existe até um minuto em que nada acontece depois do clique — daí a lista ser de marcos
 * ESPERADOS, acendendo, e não uma lista que cresce.
 *
 * **`pending_gmud` NÃO bloqueia:** espera aprovação humana, que leva horas.
 */

interface Marco {
  step: string
  rotulo: string
  nota?: string
}

/** O marco que não existe no banco: é a espera do tick de 60 s do agendador. */
export const MARCO_AGENDADOR = '__agendador__'

/** Os sete marcos do caminho de sucesso, na ordem em que o backend os grava. */
export const MARCOS: Marco[] = [
  { step: 'pedido_registrado', rotulo: 'Pedido registrado' },
  { step: MARCO_AGENDADOR, rotulo: 'Aguardando o agendador', nota: 'até 60s' },
  { step: 'ipset_criado', rotulo: 'Lista de bloqueio criada' },
  // O SEGUNDO minuto de silêncio. A AWS recusa o `CreateWebACL` com
  // `WAFUnavailableEntity` enquanto o IPSet recém-criado não propaga, e quem tenta de
  // novo é o tick do agendador — então são ~60s. O backend grava
  // `aguardando_propagacao_ipset`, e até 20/08/2026 esta lista não tinha o marco: a
  // tela mostrava a lista pronta, o Web ACL cinza e NADA entre os dois. Feedback do
  // Felipe, testando em 20/08: "dá uma falsa sensação de que travou".
  //
  // O comentário no topo deste arquivo já tratava o silêncio do COMEÇO (esperando o
  // agendador). Este é o segundo, e passou batido.
  {
    step: 'aguardando_propagacao_ipset',
    rotulo: 'Aguardando propagação da lista na AWS',
    nota: 'até 60s',
  },
  { step: 'webacl_criado', rotulo: 'Web ACL criado' },
  { step: 'aguardando_propagacao_webacl', rotulo: 'Aguardando propagação na AWS',
    nota: 'depende da AWS' },
  { step: 'associado', rotulo: 'Associado ao balanceador' },
]

/**
 * Pedidos gravados ANTES de 15/08/2026 usam `aguardando_propagacao` para tudo.
 *
 * Neles o nome aparece duas vezes com sentidos diferentes — a espera do IPSet e a
 * do Web ACL — e como o mapa abaixo fica com a ÚLTIMA ocorrência, o que sobra é
 * justamente a do Web ACL. Mapear o nome histórico para o novo preserva
 * exatamente o comportamento que esses pedidos já tinham, sem caso especial.
 *
 * Pedido novo nunca passa por aqui: o backend grava o nome preciso.
 */
const NOME_HISTORICO: Record<string, string> = {
  aguardando_propagacao: 'aguardando_propagacao_webacl',
}

/**
 * Marcos da EXCLUSÃO. Lista própria, não uma variação da de cima: os dois
 * caminhos não compartilham passo algum além do registro inicial, e tentar
 * unificá-los produziria uma lista cheia de condicionais.
 */
export const MARCOS_EXCLUSAO: Marco[] = [
  { step: 'exclusao_registrada', rotulo: 'Pedido registrado' },
  { step: MARCO_AGENDADOR, rotulo: 'Aguardando o agendador', nota: 'até 60s' },
  { step: 'desassociado', rotulo: 'Recursos desassociados' },
  { step: 'aguardando_propagacao_desassociacao', rotulo: 'Aguardando propagação na AWS' },
  { step: 'webacl_excluido', rotulo: 'Web ACL excluído' },
  { step: 'ipsets_excluidos', rotulo: 'IP sets excluídos' },
  { step: 'registro_limpo', rotulo: 'Registro limpo' },
]

/** `deletion` só existe desde a MIGRATION_226; pedido antigo não tem `kind`. */
export type PedidoKind = 'provision' | 'deletion'

function marcosDe(kind: PedidoKind): Marco[] {
  return kind === 'deletion' ? MARCOS_EXCLUSAO : MARCOS
}

/**
 * A partir daqui o modal libera o fechamento.
 *
 * Na exclusão é `desassociado`, e a assimetria com a criação é deliberada: dali
 * em diante o recurso JÁ está desprotegido, e prender o operador na tela não
 * muda isso — o scheduler termina sozinho.
 */
function liberaAPartirDe(kind: PedidoKind): string {
  return kind === 'deletion' ? 'desassociado' : 'aguardando_propagacao_webacl'
}

export interface EstadoMarco {
  feito: boolean
  emEspera: boolean
  erro: boolean
}

/**
 * O estado visual de cada marco, com **conclusão monótona**.
 *
 * Um marco conta como feito se ele mesmo está `ok` **ou** se qualquer marco
 * POSTERIOR está `ok`. Uma regra, três defeitos resolvidos:
 *
 * - o passo `'wait'` que ninguém regrava (`waf_service.py:2097` e `:2431`) parava de
 *   girar só quando alguém consertasse o backend — e o modal ficava contando
 *   `17M42S` com o trabalho pronto havia 14 minutos;
 * - o marco do agendador acende nas DUAS listas sem nome de passo hardcoded: para
 *   ele, "algum posterior concluiu" é exatamente "o agendador rodou";
 * - marco pulado (Web ACL adotado grava `webacl_adotado`, não `webacl_criado`) deixa
 *   de ficar cinza para sempre.
 *
 * Vale para os pedidos JÁ gravados, sem migration. A linha do tempo segue
 * append-only, o que é virtude: ela registra o que aconteceu e quando — interpretar
 * é papel da tela.
 */
export function avaliarMarcos(
  marcos: Marco[],
  porStep: Map<string, ProvisioningStep>,
): EstadoMarco[] {
  return marcos.map((m, i) => {
    const p = m.step === MARCO_AGENDADOR ? undefined : porStep.get(m.step)
    const erro = p?.status === 'error'
    const okDepois = marcos.slice(i + 1).some(
      (posterior) => porStep.get(posterior.step)?.status === 'ok',
    )
    const feito = !erro && (p?.status === 'ok' || okDepois)
    // O agendador não tem linha no banco: ele está "em espera" desde que o primeiro
    // marco do `kind` chegou e nada posterior concluiu.
    const emEspera = !feito && !erro && (
      p?.status === 'wait'
      || (m.step === MARCO_AGENDADOR && porStep.has(marcos[0].step))
    )
    return { feito, emEspera, erro }
  })
}

/**
 * Quanto o sucesso fica visível antes de o modal sair de cena.
 *
 * Não é estética: o modal existe para contar o que aconteceu, e sumir no instante
 * do `applied` deixaria o operador sem ver a linha de sucesso — a mesma família do
 * defeito que o criou ("inferir sucesso a partir de ausência de dado").
 */
export const FECHA_EM_MS = 3000

const SEM_PROGRESSO_MS = 180_000

export interface WafProvisioningModalProps {
  /** Tenant do PEDIDO, não o da página.
   *
   * O wizard tem seletor de tenant próprio: quem provisiona para `rota-dev` com a
   * página em `commerce-hml` gerava `pedido_nao_encontrado`, porque a rota por id
   * filtra por `tenant_id`. Defeito real, achado no aceite de 14/08. */
  tenantId: number
  requestId: number
  /** Ausente = provisionamento: pedido gravado antes da MIGRATION_226 não tem `kind`. */
  kind?: PedidoKind
  statusInicial: string
  /** Motivo do bloqueio como veio no POST. Necessário porque o poll fica
   *  desligado quando o pedido nasce `blocked` — ver `enabled` abaixo. */
  errorDetailInicial?: string | null
  gmudId?: number | null
  /** Nome do Web ACL pedido, para o cabeçalho dizer o que está sendo criado. */
  desiredName?: string | null
  albName?: string | null
  onClose: () => void
}

function duracaoEntre(a?: string, b?: string): string | null {
  if (!a || !b) return null
  const ms = new Date(b).getTime() - new Date(a).getTime()
  return ms >= 0 ? formatElapsed(ms) : null
}

export function WafProvisioningModal({
  tenantId, requestId, statusInicial, errorDetailInicial, gmudId, desiredName,
  albName, onClose,
  kind = 'provision',
}: WafProvisioningModalProps) {
  const q = useQuery({
    queryKey: ['waf-provisioning-request', tenantId, requestId],
    queryFn: () => getProvisioningRequest(tenantId, requestId),
    // 2 s enquanto ABERTO. A página usa 15 s, e com 15 s aqui um passo parado por um
    // quarto de minuto parece travado — num fluxo de 6 min é a diferença entre "está
    // andando" e "quebrou".
    refetchInterval: 2000,
    enabled: statusInicial !== 'blocked',
  })

  // Relógio vivo, o mesmo do modal de Deploy.
  useTicker(1000)
  const abertoEm = useRef<number>(Date.now())

  const pedido: ProvisioningRequest | undefined = q.data
  const passos: ProvisioningStep[] = pedido?.steps ?? []
  const status = pedido?.status ?? statusInicial

  const ultimoVisto = useRef<number>(Date.now())
  const [paradoHa, setParadoHa] = useState(0)
  useEffect(() => { ultimoVisto.current = Date.now(); setParadoHa(0) }, [passos.length])
  useEffect(() => {
    const t = setInterval(() => setParadoHa(Date.now() - ultimoVisto.current), 5000)
    return () => clearInterval(t)
  }, [])

  // A ambiguidade era do MODELO, e foi consertada lá (TASK-9797/9744): hoje o
  // backend grava um nome por semântica e não regrava marco já concluído.
  //
  // O que sobra aqui é só a tradução dos pedidos ANTIGOS, e a última ocorrência
  // continua vencendo — é ela que representa o estado alcançado.
  const porStep = new Map(
    passos.map(p => [NOME_HISTORICO[p.step] ?? p.step, p] as const),
  )
  const falhou = passos.some(p => p.status === 'error') || status === 'failed'
  const terminal = falhou || status === 'applied' || status === 'blocked'
  const marcos = marcosDe(kind)
  const naPropagacao = porStep.has(liberaAPartirDe(kind))
  const esperandoGmud = status === 'pending_gmud'
  const semProgresso = paradoHa > SEM_PROGRESSO_MS
  const podeFechar = terminal || naPropagacao || esperandoGmud || semProgresso

  const estados = avaliarMarcos(marcos, porStep)

  // Fecha sozinho SÓ no sucesso. Em `failed`/`blocked` a tela é a explicação, e
  // fechá-la esconderia o motivo — a única coisa acionável ali.
  useEffect(() => {
    if (status !== 'applied') return
    const t = setTimeout(() => onClose(), FECHA_EM_MS)
    return () => clearTimeout(t)
  }, [status, onClose])

  // Título e subtítulo por `kind`. O cabeçalho dizia "Provisionando WAF" numa
  // exclusão — `marcosDe(kind)` trocava os marcos e o título ficava —, e o subtítulo
  // repetia o nome do Web ACL duas vezes. Na exclusão o alvo não é um ALB: é a conta
  // onde o recurso deixa de existir.
  const titulo = kind === 'deletion' ? 'Excluindo WAF' : 'Provisionando WAF'
  const subtitulo = kind === 'deletion'
    ? ([pedido?.account_id, pedido?.region].filter(Boolean).join(' · ') || '—')
    : (albName || pedido?.target_alb_name || '—')
  const decorrido = formatElapsed(Date.now() - abertoEm.current)

  return (
    <Modal
      opened
      onClose={podeFechar ? onClose : () => {}}
      title={<Text fw={600}>{titulo}</Text>}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="lg"
    >
      <Stack gap="md">
        {/* Cabeçalho de identidade: o que está sendo criado, e há quanto tempo. */}
        <Card withBorder padding="sm" radius="md">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon size="lg" radius="md"
                         style={{ background: COLOR.brandGradient }}>
                <IconShieldCheck size={18} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>{desiredName || 'Web ACL'}</Text>
                <Text size="xs" c="dimmed">{subtitulo}</Text>
              </div>
            </Group>
            <Badge variant="light" leftSection={<IconClock size={12} />}>{decorrido}</Badge>
          </Group>
        </Card>

        {esperandoGmud ? (
          <Alert color="blue" title="Aguardando aprovação de GMUD">
            <Text size="sm">
              A organização deste tenant exige controle de mudança, então o provisionamento
              só executa depois da aprovação. {gmudId ? <>Mudança <Code>#{gmudId}</Code>.</> : null}
            </Text>
          </Alert>
        ) : status === 'blocked' ? (
          <Alert color="orange" title="Sem permissão para provisionar">
            <Text size="sm">
              {pedido?.error_detail || errorDetailInicial
                || 'A role do ConsoleAI nesta conta não pode criar Web ACL.'}
              {' '}O pedido não foi enviado à AWS.
            </Text>
          </Alert>
        ) : (
          <Timeline active={passos.length - 1} bulletSize={22} lineWidth={2}>
            {marcos.map((m, i) => {
              const p = m.step === MARCO_AGENDADOR ? undefined : porStep.get(m.step)
              const { feito, emEspera, erro } = estados[i]
              const anterior = i > 0 ? porStep.get(marcos[i - 1].step) : undefined
              // So mostra duracao quando o marco anterior REALMENTE veio antes: com
              // nomes reusados, a referencia pode ser posterior e a conta viraria
              // negativa ou herdaria uma espera que nao e desta etapa.
              const dur = p && anterior && anterior.id < p.id
                ? duracaoEntre(anterior.created_at, p.created_at) : null

              return (
                <Timeline.Item
                  key={m.step}
                  lineVariant={feito ? 'solid' : 'dashed'}
                  bullet={
                    <ThemeIcon size={22} radius="xl" variant={feito || emEspera ? 'filled' : 'light'}
                               color={erro ? 'red' : feito ? 'teal' : emEspera ? 'blue' : 'gray'}>
                      {/* Loader do Mantine, não IconLoader2 do tabler — aquele é
                          SVG estático e o marco "em espera" ficava congelado no
                          modal que existe justamente para mostrar o
                          provisionamento acontecendo (TASK-9817). Branco porque
                          o ThemeIcon aqui é `filled` azul. */}
                      {erro ? <IconAlertTriangle size={12} />
                        : feito ? <IconCheck size={12} />
                          : emEspera ? <Loader size={12} color="white" />
                            : <span />}
                    </ThemeIcon>
                  }
                  title={
                    <Group gap={6} justify="space-between" wrap="nowrap">
                      <Text size="sm" c={feito || emEspera ? undefined : 'dimmed'}>
                        {m.rotulo}
                        {m.nota && <Text span size="xs" c="dimmed"> ({m.nota})</Text>}
                      </Text>
                      {dur && <Text size="xs" c="dimmed">{dur}</Text>}
                    </Group>
                  }
                >
                  {/* A mensagem de operação do backend, quando existe: e é ela que diz o
                      que de fato aconteceu, em vez de repetir o rótulo. */}
                  {p?.message && <Text size="xs" c="dimmed">{p.message}</Text>}
                  {!!p?.attempts && p.attempts > 1 && (
                    <Text size="xs" c="dimmed">{p.attempts} tentativas</Text>
                  )}
                </Timeline.Item>
              )
            })}
          </Timeline>
        )}

        {falhou && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Falha ao provisionar">
            <Text size="sm">{pedido?.error_detail || 'motivo não registrado'}</Text>
          </Alert>
        )}

        {q.isError && (
          <Text size="sm" c="red">{formatApiError(q.error, 'Erro ao ler o andamento.')}</Text>
        )}

        {semProgresso && !terminal && (
          <Text size="sm" c="dimmed">
            Sem atualização há {Math.floor(paradoHa / 60000)} min. O pedido pode estar
            aguardando a AWS; o cartão na tela de WAF mostra o desfecho.
          </Text>
        )}

        {podeFechar && !terminal && (
          <Text size="sm" c="dimmed">
            Pode fechar — <b>continua rodando</b> no servidor, e o resultado aparece no
            cartão da tela de WAF. Fechar não cancela.
          </Text>
        )}

        <Group justify="flex-end">
          <Button size="xs" onClick={onClose} disabled={!podeFechar}>
            {terminal ? 'Fechar' : 'Fechar e acompanhar depois'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
